// ===== Currency Formatting =====

/**
 * Format a number as Japanese Yen (no decimals)
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('ja-JP', {
    style: 'currency',
    currency: 'JPY',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Format a number with comma separators
 */
export function formatNumber(n: number): string {
  return new Intl.NumberFormat('ja-JP').format(n);
}

// ===== Date Formatting =====

function parseDateForDisplay(dateStr: string): Date | null {
  if (!dateStr) return null;
  const dateOnly = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) {
    const [, y, m, d] = dateOnly;
    const date = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)));
    return isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Format ISO date string to Japanese locale (YYYY年MM月DD日)
 */
export function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const d = parseDateForDisplay(dateStr);
  if (!d) return dateStr;
  return new Intl.DateTimeFormat('ja-JP', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(d);
}

/**
 * Format ISO date string to YYYY/MM/DD
 */
export function formatDateShort(dateStr: string): string {
  if (!dateStr) return '';
  const d = parseDateForDisplay(dateStr);
  if (!d) return dateStr;
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}/${m}/${day}`;
}

/**
 * Format date to YYYY-MM-DD for input[type=date]
 */
export function toInputDate(dateStr: string): string {
  if (!dateStr) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

/**
 * Get today's date as YYYY-MM-DD
 */
export function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Get first day of current month as YYYY-MM-DD
 */
export function firstDayOfMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

/**
 * Get last day of current month as YYYY-MM-DD
 */
export function lastDayOfMonth(): string {
  const d = new Date();
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return last.toISOString().slice(0, 10);
}

// ===== Ratio / Percentage Formatting =====

export function formatRatio(ratio: number): string {
  return `${new Intl.NumberFormat('ja-JP', {
    minimumFractionDigits: 0,
    maximumFractionDigits: MAX_PERCENT_DECIMAL_PLACES,
  }).format(ratio * 100)}%`;
}

export const MAX_PERCENT_DECIMAL_PLACES = 3;
export const MAX_RATIO_DECIMAL_PLACES = MAX_PERCENT_DECIMAL_PLACES + 2;

export function decimalPlaces(value: number): number {
  if (!Number.isFinite(value)) return Number.POSITIVE_INFINITY;
  const text = Math.abs(value).toString().toLowerCase();
  if (!text.includes('e')) {
    return (text.split('.')[1] ?? '').replace(/0+$/, '').length;
  }
  const [coefficient, exponentText] = text.split('e');
  const exponent = Number(exponentText);
  const coefficientDecimals = (coefficient.split('.')[1] ?? '').replace(/0+$/, '').length;
  return Math.max(0, coefficientDecimals - exponent);
}

export function hasMaxDecimalPlaces(value: number, maxDecimalPlaces = MAX_RATIO_DECIMAL_PLACES): boolean {
  return decimalPlaces(value) <= maxDecimalPlaces;
}

export function normalizeRatioPrecision(value: number): number {
  if (!Number.isFinite(value)) return value;
  return Number(value.toFixed(MAX_RATIO_DECIMAL_PLACES));
}

export function parseRatioInput(value: string): number {
  const trimmed = value.trim();
  if (!trimmed) return 0;

  const fractionMatch = trimmed.match(/^([+-]?\d+(?:\.\d+)?)\s*[\/／]\s*([+-]?\d+(?:\.\d+)?)$/);
  if (fractionMatch) {
    const numerator = Number(fractionMatch[1]);
    const denominator = Number(fractionMatch[2]);
    if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator === 0) return NaN;
    return normalizeRatioPrecision(numerator / denominator);
  }

  const next = Number(trimmed);
  return Number.isNaN(next) ? NaN : next;
}

export function ratioPrecisionMessage(label = '比率'): string {
  return `${label}は%換算で小数点第${MAX_PERCENT_DECIMAL_PLACES}位までで入力してください`;
}

export function roundCurrencyAmount(amount: number, mode: TaxRoundingMode): number {
  switch (mode) {
    case 'ceil':
      return Math.ceil(amount);
    case 'round':
      return Math.round(amount);
    case 'floor':
    default:
      return Math.floor(amount);
  }
}

export const taxRoundingModeLabel: Record<TaxRoundingMode, string> = {
  floor: '切り捨て',
  round: '四捨五入',
  ceil: '切り上げ',
};

// ===== Project Tree Utilities =====

import type { Counterparty, Project, ProjectSummary, TaxRoundingMode } from '../types';

export function buildProjectTree(projects: Project[]): Project[] {
  const map = new Map<string, Project>();
  const roots: Project[] = [];

  for (const p of projects) {
    map.set(p.id, { ...p, children: [] });
  }

  for (const p of map.values()) {
    if (p.parent_id && map.has(p.parent_id)) {
      const parent = map.get(p.parent_id);
      if (!parent) continue;
      parent.children = parent.children ?? [];
      parent.children.push(p);
    } else {
      roots.push(p);
    }
  }

  return roots;
}

export function flattenTree(tree: Project[]): Project[] {
  const result: Project[] = [];
  function walk(nodes: Project[]) {
    for (const n of nodes) {
      result.push(n);
      if (n.children?.length) walk(n.children);
    }
  }
  walk(tree);
  return result;
}

export function getProjectPath(projects: Project[], projectId: string): string {
  const map = new Map(projects.map(p => [p.id, p]));
  const parts: string[] = [];
  let current = map.get(projectId);
  while (current) {
    parts.unshift(current.name);
    current = current.parent_id ? map.get(current.parent_id) : undefined;
  }
  return parts.join(' > ');
}

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export function projectOptionsForSelect(projects: Project[], excludeId?: string, includeArchived = false): SelectOption[] {
  const excluded = new Set<string>();
  if (excludeId) {
    const collect = (targetId: string) => {
      excluded.add(targetId);
      projects
        .filter(project => project.parent_id === targetId)
        .forEach(project => collect(project.id));
    };
    collect(excludeId);
  }

  const selectableProjects = includeArchived
    ? projects
    : projects.filter(project => !project.archived_at);

  return flattenTree(buildProjectTree(selectableProjects))
    .filter(project => !excluded.has(project.id))
    .map(project => ({
      value: project.id,
      label: `${getProjectPath(projects, project.id) || project.name}${project.archived_at ? '（アーカイブ）' : ''}`,
    }));
}

export function counterpartyOptionsForSelect(counterparties: Counterparty[]): SelectOption[] {
  return counterparties.map(counterparty => ({
    value: counterparty.id,
    label: counterparty.name,
  }));
}

export function buildSummaryTree(summaries: ProjectSummary[]): ProjectSummary[] {
  const map = new Map<string, ProjectSummary>();
  const roots: ProjectSummary[] = [];

  for (const s of summaries) {
    map.set(s.project_id, { ...s, children: [] });
  }

  for (const s of map.values()) {
    if (s.parent_id && map.has(s.parent_id)) {
      const parent = map.get(s.parent_id);
      if (!parent) continue;
      parent.children = parent.children ?? [];
      parent.children.push(s);
    } else {
      roots.push(s);
    }
  }

  return roots;
}

// ===== CSV Export =====

export function downloadCSV(filename: string, rows: string[][]): void {
  const bom = '﻿';
  const csv = bom + rows.map(row =>
    row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
  ).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function downloadJSON(filename: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ===== Misc =====

export function classNames(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

export function truncate(str: string, maxLen: number): string {
  if (str.length <= maxLen) return str;
  return str.slice(0, maxLen) + '…';
}

export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return '予期しないエラーが発生しました';
}

export type FieldValidator<V> = (value: V) => string | null;

export function combineValidators<V>(...fieldValidators: FieldValidator<V>[]): FieldValidator<V> {
  return value => {
    for (const validator of fieldValidators) {
      const message = validator(value);
      if (message) return message;
    }
    return null;
  };
}

export const validators = {
  required: (label: string) => (value: unknown) =>
    value === null || value === undefined || (typeof value === 'string' && !value.trim())
      ? `${label}は必須です`
      : null,

  positiveInt: (label: string) => (value: number) =>
    !Number.isFinite(value) || value <= 0 ? `${label}は1以上の整数を入力してください` : null,

  ratio: (message = '比率は0〜1の範囲で入力してください') => (value: number | undefined) => {
    const ratio = value ?? 0;
    if (!Number.isFinite(ratio)) return '比率は数値で入力してください';
    if (ratio < 0 || ratio > 1) return message;
    if (!hasMaxDecimalPlaces(ratio)) return ratioPrecisionMessage('比率');
    return null;
  },

  dateFormat: () => (value: string | undefined) =>
    value && !/^\d{4}-\d{2}-\d{2}$/.test(value)
      ? '日付はYYYY-MM-DD形式で入力してください'
      : null,
};

export const counterpartyTypeLabel: Record<string, string> = {
  corporation: '法人',
  individual: '個人',
  event: 'イベント',
};

export const formulaTypeLabel: Record<string, string> = {
  ratio: '比率按分',
  fixed: '固定金額',
  tiered: '段階的比率',
};
